// src/shared/services/advancedChartsService.js
import dayjs from 'dayjs';

export const advancedChartsService = {
    // Tạo dữ liệu cho bản đồ nhiệt
    generateHeatmapData(debts, payments) {
        const heatmapData = [];
        const startDate = dayjs().subtract(12, 'month');
        const endDate = dayjs();

        // Tạo dữ liệu cho mỗi ngày
        let currentDate = startDate;
        while (currentDate.isBefore(endDate)) {
            const dateStr = currentDate.format('YYYY-MM-DD');

            // Tính tổng nợ mới trong ngày
            const dailyNewDebts = debts.filter(debt =>
                dayjs(debt.ngayVay).format('YYYY-MM-DD') === dateStr
            ).reduce((sum, debt) => sum + debt.soTien, 0);

            // Tính tổng thanh toán trong ngày
            const dailyPayments = payments.filter(payment =>
                dayjs(payment.ngayTra).format('YYYY-MM-DD') === dateStr
            ).reduce((sum, payment) => sum + payment.soTienTra, 0);

            // Tính net flow (âm = nợ mới, dương = thanh toán)
            const netFlow = dailyPayments - dailyNewDebts;

            heatmapData.push({
                date: dateStr,
                count: Math.abs(netFlow) / 100000, // Chia để scale phù hợp
                value: netFlow,
                newDebts: dailyNewDebts,
                payments: dailyPayments
            });

            currentDate = currentDate.add(1, 'day');
        }

        return heatmapData;
    },

    // Phân tích xu hướng
    analyzeTrends(debts, payments) {
        const monthlyData = this.getMonthlyData(debts, payments);

        // Tính xu hướng nợ mới
        const debtTrend = this.calculateTrend(monthlyData.map(m => m.newDebts));

        // Tính xu hướng thanh toán
        const paymentTrend = this.calculateTrend(monthlyData.map(m => m.payments));

        // Tính xu hướng số dư nợ
        const balanceTrend = this.calculateTrend(monthlyData.map(m => m.balance));

        return {
            debt: {
                trend: debtTrend.trend,
                percentage: debtTrend.percentage,
                direction: debtTrend.direction
            },
            payment: {
                trend: paymentTrend.trend,
                percentage: paymentTrend.percentage,
                direction: paymentTrend.direction
            },
            balance: {
                trend: balanceTrend.trend,
                percentage: balanceTrend.percentage,
                direction: balanceTrend.direction
            },
            monthlyData
        };
    },

    // So sánh với tháng trước
    compareWithLastMonth(debts, payments) {
        const currentMonth = dayjs().startOf('month');
        const lastMonth = currentMonth.subtract(1, 'month');

        // Dữ liệu tháng hiện tại
        const currentMonthDebts = debts.filter(debt =>
            dayjs(debt.ngayVay).isSame(currentMonth, 'month')
        );
        const currentMonthPayments = payments.filter(payment =>
            dayjs(payment.ngayTra).isSame(currentMonth, 'month')
        );

        // Dữ liệu tháng trước
        const lastMonthDebts = debts.filter(debt =>
            dayjs(debt.ngayVay).isSame(lastMonth, 'month')
        );
        const lastMonthPayments = payments.filter(payment =>
            dayjs(payment.ngayTra).isSame(lastMonth, 'month')
        );

        const comparison = {
            newDebts: {
                current: currentMonthDebts.reduce((sum, debt) => sum + debt.soTien, 0),
                last: lastMonthDebts.reduce((sum, debt) => sum + debt.soTien, 0),
                count: {
                    current: currentMonthDebts.length,
                    last: lastMonthDebts.length
                }
            },
            payments: {
                current: currentMonthPayments.reduce((sum, payment) => sum + payment.soTienTra, 0),
                last: lastMonthPayments.reduce((sum, payment) => sum + payment.soTienTra, 0),
                count: {
                    current: currentMonthPayments.length,
                    last: lastMonthPayments.length
                }
            }
        };

        // Tính phần trăm thay đổi
        comparison.newDebts.changePercent = this.calculateChangePercent(
            comparison.newDebts.last,
            comparison.newDebts.current
        );
        comparison.payments.changePercent = this.calculateChangePercent(
            comparison.payments.last,
            comparison.payments.current
        );

        return comparison;
    },

    // Dự báo dòng tiền
    forecastCashFlow(debts, payments) {
        const futureMonths = 6;
        const forecast = [];

        // Lấy dữ liệu lịch sử 6 tháng
        const historicalData = this.getMonthlyData(debts, payments, 6);

        // Tính trung bình xu hướng
        const avgNewDebt = historicalData.reduce((sum, m) => sum + m.newDebts, 0) / historicalData.length;
        const avgPayment = historicalData.reduce((sum, m) => sum + m.payments, 0) / historicalData.length;

        // Tính xu hướng tăng/giảm
        const debtTrend = this.calculateTrend(historicalData.map(m => m.newDebts));
        const paymentTrend = this.calculateTrend(historicalData.map(m => m.payments));

        let currentBalance = debts.reduce((sum, debt) => sum + debt.soTien - (debt.soTienDaTra || 0), 0);

        for (let i = 1; i <= futureMonths; i++) {
            const month = dayjs().add(i, 'month');

            // Dự báo nợ mới (có xu hướng)
            const forecastNewDebt = avgNewDebt * (1 + (debtTrend.percentage / 100) * i * 0.1);

            // Dự báo thanh toán (có xu hướng)
            const forecastPayment = avgPayment * (1 + (paymentTrend.percentage / 100) * i * 0.1);

            // Tính số dư dự báo
            currentBalance = currentBalance + forecastNewDebt - forecastPayment;

            forecast.push({
                month: month.format('MM/YYYY'),
                newDebts: Math.max(0, forecastNewDebt),
                payments: Math.max(0, forecastPayment),
                balance: Math.max(0, currentBalance),
                netFlow: forecastPayment - forecastNewDebt
            });
        }

        return {
            forecast,
            confidence: this.calculateConfidence(historicalData),
            recommendations: this.generateForecastRecommendations(forecast)
        };
    },

    // Helper methods
    getMonthlyData(debts, payments, months = 12) {
        const monthlyData = [];

        for (let i = months - 1; i >= 0; i--) {
            const month = dayjs().subtract(i, 'month');

            const monthDebts = debts.filter(debt =>
                dayjs(debt.ngayVay).isSame(month, 'month')
            );

            const monthPayments = payments.filter(payment =>
                dayjs(payment.ngayTra).isSame(month, 'month')
            );

            const newDebts = monthDebts.reduce((sum, debt) => sum + debt.soTien, 0);
            const paymentsAmount = monthPayments.reduce((sum, payment) => sum + payment.soTienTra, 0);

            monthlyData.push({
                month: month.format('MM/YYYY'),
                newDebts,
                payments: paymentsAmount,
                balance: newDebts - paymentsAmount,
                debtCount: monthDebts.length,
                paymentCount: monthPayments.length
            });
        }

        return monthlyData;
    },

    calculateTrend(data) {
        if (data.length < 2) return { trend: 0, percentage: 0, direction: 'stable' };

        const firstHalf = data.slice(0, Math.floor(data.length / 2));
        const secondHalf = data.slice(Math.floor(data.length / 2));

        const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
        const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;

        const trend = secondAvg - firstAvg;
        const percentage = firstAvg !== 0 ? (trend / firstAvg) * 100 : 0;

        let direction = 'stable';
        if (Math.abs(percentage) > 5) {
            direction = percentage > 0 ? 'increasing' : 'decreasing';
        }

        return { trend, percentage, direction };
    },

    calculateChangePercent(oldValue, newValue) {
        if (oldValue === 0) return newValue > 0 ? 100 : 0;
        return ((newValue - oldValue) / oldValue) * 100;
    },

    calculateConfidence(historicalData) {
        // Tính độ tin cậy dựa trên độ ổn định của dữ liệu
        const values = historicalData.map(m => m.balance);
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);

        // Confidence từ 0-100%, càng ít biến động càng tin cậy
        const confidence = Math.max(0, Math.min(100, 100 - (stdDev / mean) * 50));

        return Math.round(confidence);
    },

    generateForecastRecommendations(forecast) {
        const recommendations = [];

        // Kiểm tra xu hướng số dư
        const finalBalance = forecast[forecast.length - 1].balance;
        const initialBalance = forecast[0].balance;

        if (finalBalance > initialBalance * 1.2) {
            recommendations.push('Nợ dự báo tăng cao, nên hạn chế vay mới');
        }

        if (forecast.some(f => f.netFlow < 0)) {
            recommendations.push('Có tháng dự báo nợ mới > thanh toán, cần chuẩn bị tài chính');
        }

        const avgNetFlow = forecast.reduce((sum, f) => sum + f.netFlow, 0) / forecast.length;
        if (avgNetFlow > 0) {
            recommendations.push('Xu hướng thanh toán tích cực, tiếp tục duy trì');
        }

        return recommendations;
    }
};
